/**
 * Safe redirect and navigation path sanitisation.
 *
 * Prevents open-redirect vulnerabilities where an untrusted or external URL
 * (e.g. from a push notification payload) could trigger an off-origin navigation
 * via protocol-relative URLs (`//evil.example`) or backslash bypasses (`/\evil.example`).
 * British English project.
 */

/**
 * Sanitise a path for in-app navigation or redirects.
 * Only relative paths starting with a single '/' (and not '//' or '/\') are accepted.
 * All other values safely fall back to the provided fallback path (defaulting to '/').
 */
export function sanitizeRedirectPath(rawPath: unknown, fallback = '/'): string {
  const safeFallback =
    typeof fallback === 'string' &&
    fallback.startsWith('/') &&
    !fallback.startsWith('//') &&
    !fallback.startsWith('/\\')
      ? fallback
      : '/';

  if (typeof rawPath !== 'string') {
    return safeFallback;
  }

  const trimmed = rawPath.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.startsWith('/\\')) {
    return safeFallback;
  }

  return trimmed;
}
